import { SupabaseClient } from '@supabase/supabase-js';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';

import type { RoastData } from 'features/roast/utils';
import { TanstackQueryName } from 'types/frontend';
import { Database } from 'types/supabase';

export type RoastPeriod = 'daily' | 'weekly' | 'monthly' | 'yearly';

const getPeriodStartDate = (period: RoastPeriod): string => {
  const now = new Date();
  switch (period) {
    case 'daily':
      return now.toISOString().split('T')[0];
    case 'weekly': {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return weekAgo.toISOString().split('T')[0];
    }
    case 'monthly': {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return monthAgo.toISOString().split('T')[0];
    }
    case 'yearly':
      return `${now.getFullYear()}-01-01`;
  }
};

interface IGetAllRoastsQueryFnParams {
  supabase: SupabaseClient<Database>;
  limit: number;
  period?: RoastPeriod;
}

const getAllRoastsQueryFn = async ({
  supabase,
  limit,
  period,
}: IGetAllRoastsQueryFnParams): Promise<RoastData[]> => {
  let query = supabase
    .from('roast')
    .select(
      'id, slug, url, user_id, screenshot_url, status, reveal_date, site_name, site_description, site_logo_url, detected_tech, category, pricing_model, tags, socials, contact_email, scores, roast_text, summary, upvote_count, comment_count, submitted_at, updated_at, is_promoted'
    )
    .in('status', ['ready', 'processing']);

  if (period) {
    const startDate = getPeriodStartDate(period);
    query = query.gte('submitted_at', `${startDate}T00:00:00`);
  }

  const { data, error } = await query
    .order('submitted_at', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (data ?? []) as unknown as RoastData[];
};

interface IGetAllRoastsQueryParams {
  limit?: number;
  period?: RoastPeriod;
}

interface IGetAllRoastsQueryResponse {
  data: RoastData[] | null;
  isPending: boolean;
}

export const useGetAllRoastsQuery = ({
  limit = 50,
  period,
}: IGetAllRoastsQueryParams = {}): IGetAllRoastsQueryResponse => {
  const supabase = useSupabaseTypedClient();

  const { data, isPending } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetAllRoasts, limit, period],
    queryFn: () =>
      getAllRoastsQueryFn({
        supabase,
        limit,
        period,
      }),
  });

  return {
    data: data ?? null,
    isPending,
  };
};
